import { Building2, Check, Globe } from 'lucide-react';
import type { ReactNode } from 'react';
import { MenuGroup, MenuItem, MenuSeparator, Popover, usePopover } from '../components/Popover';
import { VentureLogo } from '../components/VentureLogo';
import {
  ALL_SCOPE,
  isScopeNonDefault,
  scopeLabel,
  useRequiredSession,
  useSession,
  type Venture,
} from '../stores/session';
import styles from './UtilityBar.module.css';

// F20.05 — the scope switch. It filters the entire application.
//
// 06-qol §8: "The scope switcher state must be unmissable when non-default.
// Acting on the wrong venture's data is the highest-cost navigation bug in this
// system." So the indicator is two signals at once, not one: this pill turns solid
// accent, AND the header's bottom hairline becomes an accent rule across the full
// width (see AppShell). Either one alone is missable at a glance.

export function ScopeSwitcher(): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const session = useRequiredSession();
  const scope = useSession((state) => state.scope);
  const setScope = useSession((state) => state.setScope);

  const isNonDefault = isScopeNonDefault(scope);
  // The label comes from the store rather than the session payload, because since
  // S5 the scope may name a department or a team — levels `/auth/me` never carries.
  const label = scopeLabel(session, scope);
  // A venture shows its own logo when it has one (DECISIONS-LOG 2026-10-06); a department
  // or team keeps the building icon.
  const scopedVenture = scope.kind === 'venture' ? (session.ventures.find((v) => v.id === scope.id) ?? null) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.scope} ${isNonDefault ? styles.scopeActive : ''}`}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        // Spoken in full even when the name is visually collapsed at mobile.
        aria-label={`Scope: ${label}. Change scope`}
        title={isNonDefault ? `Filtered to ${label}` : 'All ventures'}
      >
        <ScopeIcon venture={scopedVenture} isNonDefault={isNonDefault} />
        <span className={styles.scopeName}>{label}</span>
      </button>

      <Popover anchor={anchor} onClose={close} label="Switch scope">
        <MenuGroup label="Scope">
          <MenuItem
            icon={<Globe size={15} />}
            isSelected={!isNonDefault}
            meta={!isNonDefault ? <Check size={14} /> : undefined}
            onClick={() => {
              setScope(ALL_SCOPE);
              close();
            }}
          >
            All ventures
          </MenuItem>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup label="Ventures">
          {session.ventures.map((item) => (
            <MenuItem
              key={item.id}
              icon={<VentureLogo venture={item} />}
              isSelected={scope.kind === 'venture' && scope.id === item.id}
              meta={
                scope.kind === 'venture' && scope.id === item.id ? <Check size={14} /> : item.code
              }
              onClick={() => {
                setScope({ kind: 'venture', id: item.id });
                close();
              }}
            >
              {item.name}
            </MenuItem>
          ))}
        </MenuGroup>
      </Popover>
    </>
  );
}

function ScopeIcon({ venture, isNonDefault }: { readonly venture: Venture | null; readonly isNonDefault: boolean }): ReactNode {
  if (venture) return <VentureLogo venture={venture} />;
  if (isNonDefault) return <Building2 size={15} aria-hidden />;
  return <Globe size={15} aria-hidden />;
}
